import React, { useState, useEffect, useCallback } from 'react';
import { Popover, ArrowContainer } from 'react-tiny-popover';
import { useTranslation, Trans } from 'react-i18next';
import QuestionMarkIcon from '../../assets/icons/QuestionMarkIcon';
import { COLORS } from '../../styles/variables';
import { Container, Title, Copy, IconWrap } from './InfoPopover.styles';
import InfoIcon from '../../assets/icons/InfoIcon';

interface InfoPopoverProps {
  title?: string;
  description: string;
  iconSize?: number;
  iconColor?: string;
  triggerTestId?: string;
  iconElevated?: boolean;
  parentElement?: HTMLDivElement | undefined;
  popoverAlign?: 'start' | 'center' | 'end';
}

const InfoPopover: React.FC<InfoPopoverProps> = ({
  title,
  description,
  iconSize,
  iconColor,
  triggerTestId,
  iconElevated,
  parentElement,
  popoverAlign,
}) => {
  const { t } = useTranslation();

  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const closePopover = useCallback(() => {
    setShowInfoPopover(false);
  }, []);

  const togglePopover = useCallback(() => {
    setShowInfoPopover((value) => !value);
  }, []);

  useEffect(() => {
    if (!showInfoPopover)
      return () => {
        // noop
      };

    document.addEventListener('click', closePopover);

    return () => {
      document.removeEventListener('click', closePopover);
    };
  }, [closePopover, showInfoPopover]);

  return (
    <Popover
      isOpen={showInfoPopover}
      positions={['top']}
      align={popoverAlign || 'end'}
      padding={0}
      onClickOutside={closePopover}
      containerStyle={{ zIndex: '2000' }}
      parentElement={parentElement}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor={COLORS.grey80}
          arrowSize={10}
          arrowStyle={{ opacity: 1 }}
          className="popover-arrow-container"
          arrowClassName="popover-arrow"
        >
          <Container data-testid="info-popover-container">
            {title && <Title>{title}</Title>}
            <Copy>
              <Trans
                i18nKey={description}
                // the global reset css imported in several feature apps
                // contains globally applied oppinionated styling
                components={{ italic: <span style={{ fontStyle: 'italic' }} /> }}
              />
            </Copy>
          </Container>
        </ArrowContainer>
      )}
    >
      <IconWrap
        onClick={togglePopover}
        aria-label={t('vlp.vehicleResults.popoverInformation')}
        data-testid={triggerTestId}
        iconElevated={iconElevated}
      >
        <InfoIcon color={iconColor || COLORS.black} size={iconSize || 16} />
      </IconWrap>
    </Popover>
  );
};

InfoPopover.defaultProps = {
  title: undefined,
  iconSize: undefined,
  iconColor: undefined,
  triggerTestId: undefined,
  iconElevated: undefined,
  popoverAlign: undefined,
};

export default InfoPopover;
